import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AuthCard, Button, LoadingSpinner } from '../../components';
import api from '../../utils/api';
import './VerifyEmail.scss';

/**
 * Verify Email page component
 * Handles email verification from link
 */
const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const response = await api.post('/auth/verify-email', { token });
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } catch (err: any) {
        setStatus('error');
        setMessage(
          err.response?.data?.message || 'Failed to verify email. The link may have expired.'
        );
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <AuthCard
      title={
        status === 'loading'
          ? 'Verifying Email...'
          : status === 'success'
          ? 'Email Verified!'
          : 'Verification Failed'
      }
      subtitle={status === 'loading' ? 'Please wait' : undefined}
    >
      <div className="verify-email-content">
        {status === 'loading' && (
          <LoadingSpinner size="medium" message="Verifying your email address..." />
        )}

        {status === 'success' && (
          <div className="success-message">
            <svg
              className="success-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p>{message}</p>
            <p className="redirect-text">Redirecting to login...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="error-message">
            <svg
              className="error-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p>{message}</p>
            <Link to="/login" className="back-link">
              <Button variant="primary" fullWidth>
                Go to Login
              </Button>
            </Link>
          </div>
        )}
      </div>
    </AuthCard>
  );
};

export default VerifyEmail;
